import React, { useEffect } from "react";
import {
  colors,
  CompletedCheckmarkIcon,
  Typography,
  XIcon,
} from "@commonsku/styles";
import {
  ProjectAction,
  ProjectActionLabel,
  ProjectActionType,
  ProjectActionPayload,
  mapActionTypetoOrderType,
} from "./types";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import repeatReportReducer, {
  selectActionTaken,
  selectDismissed,
  selectFormNumber,
  selectJobNumber,
  setActionTaken,
} from "../../../../redux/potentialRepeatReport";
import withReducers from "../../../../store/withReducers";

interface ActionTakenBadgeProps {
  dismissed: boolean;
  order_id: string;
  projectAction: ProjectActionPayload;
  client_id?: string;
}

const buildUrl = (
  jobNumber: number,
  formNumber: number,
  actionType: ProjectActionType,
  client_id: string
) => {
  if (
    actionType === ProjectAction.COPY_PRESENTATION ||
    actionType === ProjectAction.COPY_SALES_ORDER
  ) {
    const orderType = mapActionTypetoOrderType[actionType];
    return `/project/${jobNumber}/${orderType
      .toLowerCase()
      .replace(" ", "-")}/${formNumber}`;
  }

  if (actionType === ProjectAction.CREATE_TASK) {
    if (!client_id) {
      return null;
    }
    return `/client/${client_id}/activity`;
  }

  if (actionType === ProjectAction.CREATE_PROJECT) {
    return `/project/${jobNumber}`;
  }

  return null;
};
const actionConfig: Record<
  ProjectActionType | "dismissed",
  { label: string; icon: React.ReactElement; url?: string }
> = {
  [ProjectAction.COPY_PRESENTATION]: {
    label: ProjectActionLabel[ProjectAction.COPY_PRESENTATION],
    icon: <CompletedCheckmarkIcon fontSize="small" />,
  },
  [ProjectAction.CREATE_TASK]: {
    label: ProjectActionLabel[ProjectAction.CREATE_TASK],
    icon: <CompletedCheckmarkIcon fontSize="small" />,
  },
  [ProjectAction.COPY_SALES_ORDER]: {
    label: ProjectActionLabel[ProjectAction.COPY_SALES_ORDER],
    icon: <CompletedCheckmarkIcon fontSize="small" />,
  },
  [ProjectAction.CREATE_PROJECT]: {
    label: ProjectActionLabel[ProjectAction.CREATE_PROJECT],
    icon: <CompletedCheckmarkIcon fontSize="small" />,
  },
  dismissed: {
    label: "Dismissed",
    icon: <XIcon fontSize="small" color={colors.neutrals[70]} />,
  },
};

const ActionTakenBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  &&& {
    .action-label {
      color: ${colors.green[60]};
    }

    .action-label-dismissed {
      color: ${colors.neutrals[70]};
    }
  }
`;

const ActionTakenBadge: React.FC<ActionTakenBadgeProps> = ({
  projectAction,
  dismissed,
  order_id,
  client_id,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (projectAction) {
      dispatch(
        setActionTaken({
          orderId: order_id,
          actionType: projectAction.action_type,
          formNumber: projectAction.parent_form_number,
          jobNumber: projectAction.parent_job_number,
        })
      );
    }
  }, [order_id, projectAction, dispatch]);

  const actionTaken = useSelector((state) =>
    selectActionTaken(state, order_id)
  );
  const formNumber = useSelector((state) => selectFormNumber(state, order_id));
  const jobNumber = useSelector((state) => selectJobNumber(state, order_id));
  const isDismissedInAction = useSelector((state) =>
    selectDismissed(state, order_id)
  );

  // Consider real time dismissed and data load dismissed
  // Only two cases that should show dismissed
  // - real time action is dismissed
  // - data load is dismissed and real time action is undismissed
  let dismissedCombined =
    isDismissedInAction === true ||
    (dismissed && isDismissedInAction !== false);

  const config = dismissedCombined
    ? actionConfig["dismissed"]
    : actionConfig[actionTaken];

  if (!config) return null;

  const classNames = `action-label${dismissedCombined ? "-dismissed" : ""}`;

  const url = buildUrl(jobNumber, formNumber, actionTaken, client_id);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!url) {
      e.preventDefault();
    }
  };

  return (
    <ActionTakenBadgeContainer>
      {config.icon}
      {dismissedCombined ? (
        <Typography.Text className={classNames}>{config.label}</Typography.Text>
      ) : (
        <Typography.Text
          as={"a"}
          href={url}
          target={"_blank"}
          rel={"noopener noreferrer"}
          className={classNames}
          onClick={handleClick}
        >
          {config.label}
        </Typography.Text>
      )}
    </ActionTakenBadgeContainer>
  );
};

export default withReducers(ActionTakenBadge, {
  repeatReport: repeatReportReducer,
});
